import {
  getCookieWckAuthData,
  getCookieWckMktAdValue,
  setCookieWckMKtAdValue,
} from '@wconcept/wconcept-common/utils/cookie';
import { useCallback } from 'react';
import {
  addAsIsMWebDomain,
  currentUrl,
} from '@wconcept/wconcept-common/utils/domainUtil';
import {
  requestCouponDownload,
  requestUpdateReceiveAgree,
} from '@apis/renewal/coupon';
import {
  requestGetRoulette,
  requestRouletteEntry,
  requestSetEventAlarm,
  IAlarmRes,
  IRouletteEntryResult,
  requestGetEventAlarm,
} from '@apis/renewal/event';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import { ApiError } from '@utils/customErrors';
import { useToastPopupStore } from '@store/toast';
import useModalManager from './useModalManager';
import useGlobalState from './useGlobalState';
import useHistoryRouter from './useLinkTo';
import useAuthentication from './useAuthentication';

export interface IMessages {
  loginPrompt?: string;
  duplicatedMessage?: string;
  notInEventPeriod?: string;
  notFoundEventAlarm?: string;
  notFoundUser?: string;
  marketingConsentPrompt?: string;
  successMessage?: string;
  failMessage?: string;
  errorMessage?: string;
  invalidConditionMessage?: string;
  appInstallNotice?: string;
  emailUserMessage?: string;
}

export interface IEvent {
  onCompltedEvent?: () => void;
  onDuplicatedEvent?: () => void;
}

const MESSAGES: IMessages = {
  loginPrompt: '로그인을 완료하세요.<br />로그인 페이지로 이동하시겠습니까?',
  duplicatedMessage: '알림을 이미 신청하셨습니다.',
  notInEventPeriod: '이벤트 기간이 아닙니다.',
  notFoundEventAlarm: '알림 정보가 없습니다.',
  notFoundUser: '회원 정보가 없습니다.',
  marketingConsentPrompt:
    '본 이벤트는 마케팅 수신에 동의한 회원 대상의 이벤트입니다.<br>수신에 동의하고 알림 신청하시겠습니까?<br>(수신 동의 채널: SMS, 이메일, 앱푸시)',
  successMessage: '알림이 신청 되었습니다.',
  failMessage: '알람 신청에 실패했습니다.',
  invalidConditionMessage: '알람 신청 조건을 충족하지 못했습니다.',
  errorMessage: '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
  appInstallNotice:
    'APP에서 신청 가능합니다. 지금 APP에서 알림 신청하시겠습니까?',
};

export function getCouponMessage(isDownload?: boolean, message?: string) {
  if (isDownload) {
    return '쿠폰이 발급되었습니다.';
  }
  if (message) {
    return message;
  }
  return '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
}

function useWckDisplayInterface() {
  const { isMobile, appInfo } = useGlobalState();
  const isLogin = useCallback(async () => {
    const auth = await getCookieWckAuthData();
    return auth.userNo !== '0';
  }, []);
  const getUserNo = useCallback(async () => {
    const auth = await getCookieWckAuthData();
    return auth.userNo;
  }, []);
  const getUserId = useCallback(async () => {
    const auth = await getCookieWckAuthData();
    return auth.userId;
  }, []);
  const getAdYN = useCallback(async () => {
    const auth = await getCookieWckAuthData();
    return auth.adYN;
  }, []);
  const getMobile = useCallback(async () => {
    const auth = await getCookieWckAuthData();
    return auth.mobile;
  }, []);
  const getSnsType = useCallback(async () => {
    const auth = await getCookieWckAuthData();
    return auth.snsType;
  }, []);
  const getSnsTypeKr = useCallback(async () => {
    const type = await getSnsType();
    switch (type) {
      case 'C':
        return '이메일로그인';
      case 'kk':
        return '카카오';
      case 'gg':
        return '구글';
      case 'fb':
        return '페이스북';
      case 'nv':
        return '네이버';
      case 'lg':
        return '인스타그램';
      case 'ap':
        return '애플';
      default:
        return '';
    }
  }, [getSnsType]);

  const isNativeApp = useCallback(
    () => !!appInfo?.isNativeAppBoth,
    [appInfo?.isNativeAppBoth]
  );

  const { linkTo } = useHistoryRouter();
  const { openNoticeModal, openMarketingAgreeModal } = useModalManager();
  const setToastMessage = useToastPopupStore(state => state.setToastMessage);
  const { onAuthenticationrNotice } = useAuthentication();

  // 알림 팝업 표시
  const onNoticePopup = useCallback(
    async ({
      closeText = '취소',
      submitText = '확인',
      btnType = 'single',
      text,
      onEvent,
    }: {
      closeText?: string;
      submitText?: string;
      text?: string;
      btnType?: 'single' | 'double' | string;
      onEvent?: () => void;
    }) => {
      const result = await openNoticeModal({
        isMobile: !!isMobile,
        message: text || '',
        type: btnType === 'single' ? 'alert' : 'confirm',
        submitText,
        closeText,
      });
      onEvent?.();
      return Boolean(result);
    },
    [openNoticeModal, isMobile]
  );

  const alertUser = useCallback(
    (message = ''): void => {
      onNoticePopup({ text: message });
    },
    [onNoticePopup]
  );

  const handleError = useCallback(
    (error: ApiError, defaultMessage = MESSAGES.errorMessage): void => {
      const errorMsg =
        (error as ApiError & { response?: { message?: string } }).response
          ?.message ||
        error.message ||
        defaultMessage;
      onNoticePopup({ text: errorMsg });
    },
    [onNoticePopup]
  );

  const isEventPeriod = ({
    today,
    applyStartDate,
    applyEndDate,
  }: {
    today: dayjs.Dayjs;
    applyStartDate: string;
    applyEndDate: string;
  }): boolean => today.isAfter(applyStartDate) && today.isBefore(applyEndDate);

  const promptMarketingPopup = useCallback(
    async ({
      receiveAgreeType,
      message,
    }: {
      receiveAgreeType?: 'PUSH' | 'BASIC' | 'NONE';
      message?: string;
    }) => {
      let consentMessage = message;
      if (receiveAgreeType === 'BASIC') {
        consentMessage = message?.replace(', 앱푸시', '');
      }
      if (receiveAgreeType === 'PUSH') {
        consentMessage = isNativeApp()
          ? message
          : message?.replace(', 앱푸시', '');
      }
      const res = await onNoticePopup({
        text: consentMessage,
        btnType: 'double',
      });
      return res;
    },
    [isNativeApp, onNoticePopup]
  );

  const promptLogin = useCallback(
    async (message = MESSAGES.loginPrompt): Promise<boolean> => {
      if (await onNoticePopup({ text: message, btnType: 'single' })) {
        const targetUrl = `${await addAsIsMWebDomain(
          '/Member/Login'
        )}?rUrl=${encodeURIComponent(currentUrl())}`;
        linkTo.push({
          url: targetUrl,
        });
        return true;
      }
      return false;
    },
    [linkTo, onNoticePopup]
  );

  const checkLogin = useCallback(
    async ({
      message = '로그인 페이지로 이동합니다.<br />로그인 완료 후, 버튼을<br />다시 클릭해 주세요.',
      btnType = 'single',
    }: {
      message?: string;
      btnType?: 'single' | 'double' | string;
    } = {}) => {
      if (
        await onNoticePopup({
          text: message,
          btnType,
        })
      ) {
        const targetUrl = `${await addAsIsMWebDomain(
          '/Member/Login'
        )}?rUrl=${encodeURIComponent(currentUrl())}`;
        linkTo.push({
          url: targetUrl,
        });
      }
      return false;
    },
    [linkTo, onNoticePopup]
  );

  // 이벤트 알림 요청
  const onReuqestAlarm = useCallback(
    async ({
      eventId,
      sectionId,
      alarmId,
      successMessage,
      invalidConditionMessage,
      duplicatedMessage,
      onEvent,
    }: {
      eventId: number;
      sectionId: number;
      alarmId: number;
      successMessage?: string;
      invalidConditionMessage?: string;
      duplicatedMessage?: string;
      onEvent?: IEvent;
    }) => {
      try {
        if (await isLogin()) {
          const alarmRes = await requestSetEventAlarm({
            eventId,
            sectionId,
            alarmId,
            custId: await getUserId(),
            custNo: await getUserNo(),
            appYn: isNativeApp() ? 'Y' : 'N',
          });
          if (alarmRes?.result === 'SUCCESS') {
            let message = successMessage;
            if (alarmRes?.data?.applyResult === 'COMPLETED') {
              message = successMessage;
              onNoticePopup({
                text: message || MESSAGES.successMessage,
                onEvent: onEvent?.onCompltedEvent,
              });
              return;
            }
            if (alarmRes?.data?.applyResult === 'DUPLICATED') {
              message = duplicatedMessage || MESSAGES.duplicatedMessage;
              onNoticePopup({
                text: message || MESSAGES.successMessage,
                onEvent: onEvent?.onDuplicatedEvent,
              });
              return;
            }
            if (alarmRes?.data?.applyResult === 'APPLY_NOTFOUND') {
              message = MESSAGES.notFoundEventAlarm;
            }
            if (alarmRes?.data?.applyResult === 'INVALID_CUSTOMER') {
              message = MESSAGES.notFoundUser;
            }
            if (alarmRes?.data?.applyResult === 'INVALID_CONDITION') {
              message =
                invalidConditionMessage || MESSAGES.invalidConditionMessage;
            }
            onNoticePopup({
              text: message || MESSAGES.successMessage,
            });
          }
        }
      } catch (e) {
        const error = e as ApiError & { response?: { message?: string } };
        const errorMessage = error.response?.message;
        onNoticePopup({
          text:
            errorMessage ||
            '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
        });
      }
    },
    [getUserId, getUserNo, isLogin, isNativeApp, onNoticePopup]
  );

  const setAlarmAndRequest = useCallback(
    async ({
      eventId,
      sectionId,
      alarmId,
      successMessage,
      invalidConditionMessage,
      receiveAgreeType,
      duplicatedMessage,
      onEvent,
    }: {
      eventId: number;
      sectionId: number;
      alarmId: number;
      successMessage?: string;
      invalidConditionMessage?: string;
      duplicatedMessage?: string;
      receiveAgreeType?: 'PUSH' | 'BASIC' | 'NONE';
      onEvent?: IEvent;
    }) => {
      if (isNativeApp() && receiveAgreeType === 'PUSH') {
        (
          window as {
            WCKAppFunction?: {
              setAlarm?: (...args: unknown[]) => void;
            };
          }
        )?.WCKAppFunction?.setAlarm?.(true, 'enableAlarmComplete', {
          custid: await getUserId(),
          custno: await getUserNo(),
        });
      }
      onReuqestAlarm({
        eventId,
        sectionId,
        alarmId,
        successMessage,
        invalidConditionMessage,
        duplicatedMessage,
        onEvent,
      });
    },
    [getUserId, getUserNo, isNativeApp, onReuqestAlarm]
  );

  // 룰렛 가져오기
  const getRoulette = useCallback(
    async (
      sectionId: number
    ): Promise<
      | {
          rouletteId: number;
          eventId: number;
          sectionId: number;
          rouletteTitle: string;
          rouletteDesc: string;
          prizeType: string;
          entryStartDate: string;
          entryEndDate: string;
          nextRouletteYn: string;
        }
      | undefined
    > => {
      try {
        const res = await requestGetRoulette(sectionId);
        return res;
      } catch (e) {
        const error = e as ApiError & { response?: { message?: string } };
        handleError(error, error.response?.message || MESSAGES.errorMessage);
        return undefined;
      }
    },
    [handleError]
  );

  // 룰렛 응모 요청
  const reqeustRoulette = useCallback(
    async ({
      rouletteId,
      custNo,
    }: {
      rouletteId: number;
      custNo: number;
    }): Promise<IRouletteEntryResult | undefined> => {
      if (!(await isLogin())) {
        await checkLogin();
        return undefined;
      }
      try {
        const res = await requestRouletteEntry({ rouletteId, custNo });
        return res;
      } catch (e) {
        const error = e as ApiError & { response?: { message?: string } };
        handleError(error, error.response?.message || MESSAGES.errorMessage);
        return undefined;
      }
    },
    [checkLogin, handleError, isLogin]
  );

  // 이벤트 알람 정보 설정
  const setEventAlarmInfo = useCallback(
    async ({
      eventId,
      sectionId,
      alarmId,
      alarmInfo,
      options,
    }: {
      eventId: number;
      sectionId: number;
      alarmId: number;
      alarmInfo: IAlarmRes;
      options?: {
        messages: IMessages;
        isAppOnly?: boolean;
        appLinkUrl?: string;
        isEmailId?: boolean;
        onEvent?: IEvent;
      };
    }) => {
      // 앱만 가능한 이벤트
      if (options?.isAppOnly && !isNativeApp()) {
        const appInstallNoticeRes = await onNoticePopup({
          text: options?.messages.appInstallNotice || MESSAGES.appInstallNotice,
          btnType: 'double',
        });
        if (appInstallNoticeRes) {
          window.location.href = options.appLinkUrl as string;
          return;
        }
        return;
      }
      // 로그인 체크
      if (
        !(await isLogin()) &&
        (await promptLogin(options?.messages.loginPrompt))
      ) {
        return;
      }
      // 로그인 체크
      if (!(await isLogin())) return;

      // 이메일로그인 체크
      if (options?.isEmailId && (await getSnsType()) !== 'C') {
        let msg;
        // SNS로그인인데 SnsType이 없을경우 분기처리
        if ((await getSnsType()) === '') {
          msg =
            options?.messages?.emailUserMessage ||
            '이메일로 가입한 고객님께서만 응모할 수 있습니다.';
        } else {
          msg = `${await getSnsTypeKr()} 채널을 통해 가입한 회원으로 이벤트 대상자가 아닙니다.`;
        }
        await onNoticePopup({
          text: msg,
        });
        return;
      }

      try {
        // 1일용 쿠키 유저정보가 마케팅 동의되도 바로 바뀌지 않아서 쿠키로 체크
        const mktAdValueYn = await getCookieWckMktAdValue();
        const today = dayjs();
        // 이미 신청한 경우
        if (alarmInfo?.applyYn === 'Y') {
          alertUser(
            options?.messages?.duplicatedMessage || MESSAGES.duplicatedMessage
          );
        } else if (
          // 이벤트 기간이 아닌 경우
          !isEventPeriod({
            today,
            applyStartDate: alarmInfo?.applyStartDate as string,
            applyEndDate: alarmInfo?.applyEndDate as string,
          })
        ) {
          alertUser(
            options?.messages?.notInEventPeriod || MESSAGES.notInEventPeriod
          );
        } else if (
          alarmInfo?.authNecessaryYn === 'Y' &&
          isEmpty(await getMobile())
        ) {
          // 핸드폰 번호가 없는 경우
          onAuthenticationrNotice();
        } else if (
          mktAdValueYn !== 'Y' &&
          (await getAdYN()) !== 'Y' &&
          alarmInfo?.receiveAgreeType !== 'NONE'
        ) {
          // 마케팅 동의가 필요한 경우
          const isMarketingAgree = await promptMarketingPopup({
            receiveAgreeType: alarmInfo?.receiveAgreeType,
            message:
              options?.messages?.marketingConsentPrompt ||
              MESSAGES.marketingConsentPrompt,
          });
          // 마케팅 동의한 경우
          if (isMarketingAgree) {
            setCookieWckMKtAdValue('Y');
            setAlarmAndRequest({
              eventId,
              sectionId,
              alarmId,
              successMessage: options?.messages.successMessage,
              duplicatedMessage: options?.messages?.duplicatedMessage,
              invalidConditionMessage:
                options?.messages.invalidConditionMessage,
              receiveAgreeType: alarmInfo?.receiveAgreeType,
            });
          }
        } else {
          // 마케팅 동의가 필요하지 않은 경우
          setAlarmAndRequest({
            eventId,
            sectionId,
            alarmId,
            successMessage: options?.messages?.successMessage,
            invalidConditionMessage: options?.messages?.invalidConditionMessage,
            duplicatedMessage: options?.messages?.duplicatedMessage,
            receiveAgreeType: alarmInfo?.receiveAgreeType,
            onEvent: options?.onEvent,
          });
        }
      } catch (e) {
        const error = e as ApiError & { response?: { message?: string } };
        handleError(error, error.response?.message || MESSAGES.errorMessage);
      }
    },
    [
      alertUser,
      getAdYN,
      getSnsTypeKr,
      getMobile,
      getSnsType,
      handleError,
      isLogin,
      isNativeApp,
      onAuthenticationrNotice,
      onNoticePopup,
      promptLogin,
      promptMarketingPopup,
      setAlarmAndRequest,
    ]
  );

  const downloadCoupon = useCallback(
    async (couponCds: string[]) => {
      if (
        !(await isLogin()) &&
        (await openNoticeModal({
          isMobile: !!isMobile,
          message:
            '로그인 페이지로 이동합니다.<br />로그인 완료 후, 쿠폰 받기 버튼을<br />다시 클릭해 주세요.',
          type: 'alert',
          submitText: '확인',
        }))
      ) {
        const targetUrl = `${await addAsIsMWebDomain(
          '/Member/Login'
        )}?rUrl=${encodeURIComponent(currentUrl())}`;
        linkTo.push({
          url: targetUrl,
        });
        return;
      }
      try {
        if ((await isLogin()) && (await getUserNo())) {
          const res = await requestCouponDownload({
            couponCds,
            custNo: parseInt(await getUserNo(), 10),
          });
          const isAllDownload = res?.couponDownlodInfos?.some(
            couponRes => couponRes.resultCode === 0
          );
          openNoticeModal({
            isMobile: !!isMobile,
            message: getCouponMessage(
              !!isAllDownload,
              res?.couponDownlodInfos?.[0]?.resultMessage
            ),
          });
        }
      } catch (e) {
        openNoticeModal({
          isMobile: !!isMobile,
          message: MESSAGES.errorMessage || '',
        });
      }
    },
    [isLogin, openNoticeModal, linkTo, getUserNo, isMobile]
  );

  // 마케팅 수신동의 후 쿠폰 다운로드
  const downloadCouponWithMarketingAgree = useCallback(
    async (couponCds: string[]) => {
      const mktAdValueYn = await getCookieWckMktAdValue();

      // 1. 로그인 체크
      if (
        !(await isLogin()) &&
        (await openNoticeModal({
          isMobile: !!isMobile,
          message:
            '로그인 페이지로 이동합니다.<br />로그인 완료 후, 쿠폰 받기 버튼을<br />다시 클릭해 주세요.',
          type: 'alert',
          submitText: '확인',
        }))
      ) {
        const targetUrl = `${await addAsIsMWebDomain(
          '/Member/Login'
        )}?rUrl=${encodeURIComponent(currentUrl())}`;
        linkTo.push({
          url: targetUrl,
        });
        return;
      }
      if (!(await isLogin())) return;

      // 2. 마케팅 동의 체크
      const isAlreadyAgreed = mktAdValueYn === 'Y' || (await getAdYN()) === 'Y';

      if (!isAlreadyAgreed) {
        const agreeResult = await openMarketingAgreeModal();
        if (!agreeResult) {
          return;
        }

        try {
          const receiveAgreeRes = await requestUpdateReceiveAgree();
          if (receiveAgreeRes?.result === 'FAIL') {
            setToastMessage(
              receiveAgreeRes?.message || MESSAGES.errorMessage || ''
            );
            return;
          }
        } catch (e) {
          const error = e as ApiError & { response?: { message?: string } };
          setToastMessage(
            error.response?.message ||
              error.message ||
              MESSAGES.errorMessage ||
              ''
          );
          return;
        }

        // 동의 저장 성공 → 캐시 쿠키 'Y' 세팅
        await setCookieWckMKtAdValue('Y');

        // 앱 + 앱푸시 동의 시: 네이티브 앱 알람 활성화
        if (isNativeApp() && agreeResult.appPush) {
          (
            window as {
              WCKAppFunction?: {
                setAlarm?: (...args: unknown[]) => void;
              };
            }
          )?.WCKAppFunction?.setAlarm?.(true, 'enableAlarmComplete', {
            custid: await getUserId(),
            custno: await getUserNo(),
          });

          const today = dayjs().format('YYYY년 MM월 DD일');
          setToastMessage(
            `[W컨셉] ${today} 광고성<br />정보 SMS, 이메일, 푸시 수신동의 처리되었습니다.`
          );
          // 동의 안내 토스트 노출 후 발급 완료 토스트가 이어지도록 약간의 텀
          await new Promise(resolve => {
            window.setTimeout(resolve, 2000);
          });
        }
      }

      // 3. 쿠폰 다운로드
      try {
        if (await getUserNo()) {
          const res = await requestCouponDownload({
            couponCds,
            custNo: parseInt(await getUserNo(), 10),
          });
          const isAllDownload = res?.couponDownlodInfos?.some(
            couponRes => couponRes.resultCode === 0
          );
          setToastMessage(
            getCouponMessage(
              !!isAllDownload,
              res?.couponDownlodInfos?.[0]?.resultMessage
            )
          );
        }
      } catch (e) {
        setToastMessage(MESSAGES.errorMessage || '');
      }
    },
    [
      isLogin,
      openNoticeModal,
      openMarketingAgreeModal,
      setToastMessage,
      linkTo,
      getUserNo,
      getUserId,
      getAdYN,
      isMobile,
      isNativeApp,
    ]
  );

  const getEventAlarmInfo = useCallback(
    async ({ sectionId }: { sectionId: number }) => {
      const res = await requestGetEventAlarm(sectionId, await getUserNo());
      return res;
    },
    [getUserNo]
  );

  return {
    downloadCoupon,
    downloadCouponWithMarketingAgree,
    getRoulette,
    reqeustRoulette,
    setEventAlarmInfo,
    onNoticePopup,
    getUserNo,
    getEventAlarmInfo,
  };
}

export default useWckDisplayInterface;
